import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './uploadbutton.scss';

import alertify from "./../libs/alert"
import { useDispatch, useSelector } from 'react-redux';
import { clearImages, setRepeatType } from '../redux/actions/parts';
import { SEGMENT_SETTINGS_UPDATE } from '../redux/reducers/fabric/actions';
import { ACTIONS_CLEAR_ALL } from '../redux/reducers/images';
import useIframeMediaLibrary from "../hooks/useIframeMediaLibrary";
import { useCopyrightWarning } from '../hooks/useCopyrightWarning';
import { addImagesToPart } from '../redux/actions/parts/addImage';
import { ImageInfo } from '../redux/actions/parts/types/parts';
import { getAsFile, getFileId, getFileName, handleFiles } from '../redux/actions/parts/addImage.helpers';
import { setForceUploadOverlayOpen, setUploadedImgSrc } from '../redux/actions/product';

export type UploadButtonProps = {
    interceptUploadEvent?: () => void
    repeatType?: 'NON_REPEAT' | 'REPEAT'
    totalCanvasImages?: number
    copyrightCompliance?: boolean
    imageUploadInProgress?: (progress: boolean) => void
    fabricSize: { width: number, height: number }
    partId: string
}

export const UploadButton = (props: UploadButtonProps) => {

    const { interceptUploadEvent, repeatType, totalCanvasImages = 0, copyrightCompliance = true, imageUploadInProgress = () => { }, fabricSize, partId } = props
    const { t } = useTranslation();
    const [readyToUploadMode, setreadyToUploadMode] = useState(true)
    const filesPendingUpload = useRef([] as any)
    const dispatch = useDispatch();
    const minResolution = useSelector((state: any) => state.fbdata?.minimalResolution);
    const isDefault = useSelector((state: any) => state.fbdata?.isDefault)
    const selectedSegment = useSelector((state: any) => state.fabric.segmentInFocus);
    const currentImage = useSelector((state: any) => state.currentImg);

    const apikey = useSelector((state:any) => state.apikey)
    const mediaLibTitle = useSelector((state: any) => state.mediaLibTitle);

    const iFrameImageErrorHandler = useCallback((imageId)=>(error:any) => {
        console.log(error);
        alertify.alert(t("editor.alert_warning_title1"), "Pattern " + `${imageId}`+ " not found!", t("i18n.ok"), () => { 
            dispatch(setForceUploadOverlayOpen(true))
        })
    }, [])

    const firebaseImages = useIframeMediaLibrary({ userId: apikey, auth: 1, mediaLibTitle: mediaLibTitle, onMediaLibNonExistent:iFrameImageErrorHandler})

    const resolutionSetting = useSelector((state: any) => state.fbdata?.fabricAutoSizeImage);

    

    useEffect(() => {
        if (mediaLibTitle && firebaseImages && firebaseImages.length && selectedSegment) {
            getAsFile(firebaseImages[0].url).then((imagefile)=>{
                let isLibImage = true;
                const id = firebaseImages[0].id;
                (imagefile as any).id = firebaseImages[0].id;
                uploadHandler(true)({ target: { files: [imagefile] } }, firebaseImages[0].scaleFactor || 1, isDefault, isLibImage, iFrameImageErrorHandler(id))
            }).catch((error) => {
                iFrameImageErrorHandler(firebaseImages[0].id)(error)
            })
        }
    }, [firebaseImages, mediaLibTitle, selectedSegment, isDefault])

    useEffect(() => {
        if (currentImage && selectedSegment) {
            getAsFile(currentImage).then((imagefile)=>{
                console.log(imagefile);
                let isLibImage = true;
                const id = getFileId(imagefile.name);
                (imagefile as any).id = id
                uploadHandler(true)({ target: { files: [imagefile] } }, undefined, isDefault, isLibImage,iFrameImageErrorHandler(id))
            }).catch((error)=>{
                iFrameImageErrorHandler(getFileId(getFileName(currentImage)))(error)
            })
            
        }
    }, [currentImage, selectedSegment]);

    const uploaderRef = useRef(undefined as any);

    const compliant = useSelector((state: any) => state.canvasImages?.isCopyrightCompliant)
    const [selectedFiles, setSelectedFiles] = useState(undefined as File[] | undefined);


    const toggleUploadMode = useCallback(() => {
        setreadyToUploadMode(!readyToUploadMode)
    }, [readyToUploadMode]);

    const uploadHandler = useCallback((compliant: boolean) => (e: any, scaleFactor: number = 1, isDefault: boolean = false, isLibImage: boolean = false, iFrameImageErrorHandler?:(error:any)=>void) => {
        const validity = compliant;
        if (validity) {
            const files = e.target.files
            if (files.length === 0) {
                setreadyToUploadMode(true)
                e.preventDefault()
                return
            }

            imageUploadInProgress(true)
            handleFiles(files,isLibImage).then((imageExifData) => {
                const images:ImageInfo[] = imageExifData.map((data) => ({
                    ...data, minResolution, fabricSize, repeatType: 'DEFAULT_REPEAT', scaleFactor, isDefault, isLibImage, resolutionSetting,
                    image_id: isLibImage?((data.file as any)?.id||''):''
                }));
                dispatch({ type: ACTIONS_CLEAR_ALL })
                dispatch(clearImages(true))
                dispatch(addImagesToPart(partId, images, scaleFactor || 1, t, true))
                dispatch(setUploadedImgSrc(false))
                
                dispatch(setRepeatType(partId, 'DEFAULT_REPEAT', true))
                dispatch({
                    type: SEGMENT_SETTINGS_UPDATE, payload: {
                        parentCanvas: selectedSegment,
                        repeatType: 'DEFAULT_REPEAT'
                    }
                })

            }).catch((error) => {
                if(iFrameImageErrorHandler){
                    iFrameImageErrorHandler(error)
                }else{
                console.log(error);
                uploaderRef.current && (uploaderRef.current.value = null);
                alertify.alert(t("editor.alert_warning_title1"), t("editor.alert.warning_msg1"), t("i18n.ok"), () => { })
                }
            }).finally(() => {
                imageUploadInProgress(false)
            })
        } else {
            e.target.files?.length && setSelectedFiles(e.target.files)
        }
    }, [repeatType, copyrightCompliance, imageUploadInProgress, minResolution, fabricSize, partId, selectedSegment])

    const onRejection = useCallback(() => {
        uploaderRef.current && (uploaderRef.current.value = null);
        setSelectedFiles(undefined)
    }, [])

    const onAcceptance = useCallback((selectedFiles) => {
        uploadHandler(true)({ target: { files: selectedFiles } }, 1, isDefault)
    }, [uploadHandler, isDefault])

    const copyRightWarning = useCopyrightWarning(compliant, onRejection, onAcceptance, selectedFiles)

    return <div className="upload-button"
        onClick={interceptUploadEvent ? (e) => {
            if ((repeatType) !== 'NON_REPEAT' && totalCanvasImages >= 1) {
                e.preventDefault()
                alertify.alert(t("editor.alert_warning_title1"), t("editor.images_repeat_mode_add_image_msg"), t("i18n.ok"), () => { })
            } else if ((repeatType) === 'NON_REPEAT' && (totalCanvasImages) == 1) {
                e.preventDefault()
                alertify.alert(t("editor.alert_warning_title1"), t("You cannot upload more than one image."), t("i18n.ok"), () => { })
            } else {
                interceptUploadEvent()
            }
        } : undefined}>
        <div title="Upload an Image" className="uploadNewDesign"></div>
        {/* <div className='upload-button-display'>
            <span className='upload-icon'>{t("editor.custom.editor_upload_new_design_btn")}</span>
        </div> */}
        {interceptUploadEvent ? null : <input
            id="uploadAddImageBtn"
            className="upload-input-concealer"
            type="file"
            accept=".jpg,.jpeg,.png"
            title="Upload an Image"
            onChange={readyToUploadMode ? uploadHandler(compliant) : toggleUploadMode}
            onClick={() => { }}
            ref={uploaderRef}
            multiple={false} />
        }
        {t("editor.custom.upload-tile")}
        {copyRightWarning}
    </div>
}

